export const hexToRgbString = (hex: string, alpha?: number) => {
  const [r, g, b] = hexToRgb(hex);

  return rgbToString(r, g, b, alpha);
};

export const rgbToString = (
  r: number,
  g: number,
  b: number,
  alpha?: number,
) => {
  if (alpha != null) return `rgba(${r}, ${g}, ${b}, ${alpha})`;

  return `rgb(${r}, ${g}, ${b})`;
};

export const hexToRgb = (hex: string): [number, number, number] => {
  const r = parseInt(hex.substring(1, 3), 16);
  const g = parseInt(hex.substring(3, 5), 16);
  const b = parseInt(hex.substring(5, 7), 16);

  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    console.warn('Invalid hex color:', hex);
    return [0, 0, 0];
  }

  return [r, g, b];
};

export const rgbToHsb = (
  r: number,
  g: number,
  b: number,
): [number, number, number] => {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);

  const delta = max - min;
  const saturation = max === 0 ? 0 : delta / max;

  let hue: number;
  if (delta === 0) {
    hue = 0;
  } else if (max === r) {
    hue = ((g - b) / delta) % 6;
  } else if (max === g) {
    hue = (b - r) / delta + 2;
  } else {
    hue = (r - g) / delta + 4;
  }

  hue = Math.round(hue * 60);
  if (hue < 0) hue += 360;

  return [hue, Math.round(saturation * 100), Math.round((max / 255) * 100)];
};

export const hsbToRgb = (
  h: number,
  s: number,
  b: number,
): [number, number, number] => {
  const saturation = s / 100;
  const brightness = b / 100;

  const c = brightness * saturation;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = brightness - c;

  let r = 0,
    g = 0,
    bl = 0;

  if (0 <= h && h < 60) {
    r = c;
    g = x;
  } else if (60 <= h && h < 120) {
    r = x;
    g = c;
  } else if (120 <= h && h < 180) {
    g = c;
    bl = x;
  } else if (180 <= h && h < 240) {
    g = x;
    bl = c;
  } else if (240 <= h && h < 300) {
    r = x;
    bl = c;
  } else if (300 <= h && h < 360) {
    r = c;
    bl = x;
  }

  const red = Math.round((r + m) * 255);
  const green = Math.round((g + m) * 255);
  const blue = Math.round((bl + m) * 255);

  return [red, green, blue];
};

export const rgbToHsl = (
  r: number,
  g: number,
  b: number,
): [number, number, number] => {
  const rNorm = r / 255;
  const gNorm = g / 255;
  const bNorm = b / 255;

  const max = Math.max(rNorm, gNorm, bNorm);
  const min = Math.min(rNorm, gNorm, bNorm);
  const delta = max - min;

  let hue: number;
  if (delta === 0) {
    hue = 0;
  } else if (max === rNorm) {
    hue = ((gNorm - bNorm) / delta) % 6;
  } else if (max === gNorm) {
    hue = (bNorm - rNorm) / delta + 2;
  } else {
    hue = (rNorm - gNorm) / delta + 4;
  }

  hue = Math.round(hue * 60);
  if (hue < 0) hue += 360;

  const lightness = (max + min) / 2;
  let saturation = 0;
  if (delta !== 0) {
    saturation = delta / (1 - Math.abs(2 * lightness - 1));
  }

  return [hue, Math.round(saturation * 100), Math.round(lightness * 100)];
};

export const hslToRgb = (
  h: number,
  s: number,
  l: number,
): [number, number, number] => {
  const saturation = s / 100;
  const lightness = l / 100;

  const c = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = lightness - c / 2;

  let r = 0,
    g = 0,
    bl = 0;

  if (0 <= h && h < 60) {
    r = c;
    g = x;
  } else if (60 <= h && h < 120) {
    r = x;
    g = c;
  } else if (120 <= h && h < 180) {
    g = c;
    bl = x;
  } else if (180 <= h && h < 240) {
    g = x;
    bl = c;
  } else if (240 <= h && h < 300) {
    r = x;
    bl = c;
  } else if (300 <= h && h < 360) {
    r = c;
    bl = x;
  }

  const red = Math.round((r + m) * 255);
  const green = Math.round((g + m) * 255);
  const blue = Math.round((bl + m) * 255);

  return [red, green, blue];
};
